// Angular
import { Injectable } from '@angular/core';
// RxJS
import { of, Observable, defer, forkJoin } from 'rxjs';
import { mergeMap, map, withLatestFrom, filter, catchError } from 'rxjs/operators';
// NGRX
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Store, select, Action } from '@ngrx/store';
// Services
import { AgreementService } from '../_services';
// State
import { AppState } from '../../../core/reducers';
// Selectors
// Actions
import {
  AllAgreementsLoaded,
  AllAgreementsRequested,
  AgreementActionTypes,
  AgreementsPageRequested,
  AgreementReminderRequested,
  AgreementsPageLoaded,
  AgreementUpdated,
  AgreementsPageToggleLoading,
  AgreementDeleted,
  AgreementsDeleted,
  AgreementOnServerCreated,
  AgreementCreated,
  AgreementReminded,
  AgreementRevoked,
  AgreementsActionToggleLoading,
  AgreementsPageCancelled,
  ThrowAgreementError,
  FireAgreementSuccess,
  AgreementOnServerDeleted,
  AgreementsOnServerDeleted,
  AgreementOnServerUpdated,
  RevokeAgreementRequested,
} from '../_actions/agreement.actions';
import { currentUser } from '../../auth/_selectors/auth.selectors';

@Injectable()
export class AgreementEffects {
  showPageLoadingDistpatcher = new AgreementsPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new AgreementsPageToggleLoading({ isLoading: false });

  showActionLoadingDistpatcher = new AgreementsActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new AgreementsActionToggleLoading({ isLoading: false });

  @Effect()
  loadAllAgreements$ = this.actions$.pipe(
    ofType<AllAgreementsRequested>(AgreementActionTypes.AllAgreementsRequested),
    withLatestFrom(this.store.pipe(select(currentUser))), // Get current user to check for companyId
    filter(([, user]) => !!user?.companyID), // Only proceed if companyID is available
    mergeMap(([, user]) => {
      // Proceed to make the request if companyID is available
      return this.agreementService.getAllAgreements(user.companyID).pipe(
        map((agreements) => {
          // Store the templates in localStorage
          localStorage.setItem('allAgreement', JSON.stringify(agreements));

          // Dispatch the AllAgreementTemplatesLoaded action with the fetched templates
          return new AllAgreementsLoaded({ agreements });
        }),
      );
    }),
  );

  @Effect()
  loadAgreementsPage$ = this.actions$.pipe(
    ofType<AgreementsPageRequested>(AgreementActionTypes.AgreementsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.agreementService.findAgreements(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.length === 2) {
            const result = res[0];
            return new AgreementsPageLoaded({
              agreements: result.data,
              totalCount: result.count,
            });
          } else {
            this.store.dispatch(new ThrowAgreementError({ error: res }));
            return new AgreementsPageCancelled();
          }
        }),
      );
    }),
  );

  @Effect()
  deleteAgreement$ = this.actions$.pipe(
    ofType<AgreementOnServerDeleted>(AgreementActionTypes.AgreementOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.deleteAgreement(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowAgreementError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementDeleted }),
            );
            return new AgreementDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deleteAgreements$ = this.actions$.pipe(
    ofType<AgreementsOnServerDeleted>(AgreementActionTypes.AgreementsOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.deleteAgreements(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowAgreementError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementsDeleted }),
            );
            return new AgreementsDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateAgreement$ = this.actions$.pipe(
    ofType<AgreementOnServerUpdated>(AgreementActionTypes.AgreementOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.updateAgreement(payload.agreement).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowAgreementError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementUpdated }),
            );
            return new AgreementUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createAgreement$ = this.actions$.pipe(
    ofType<AgreementOnServerCreated>(AgreementActionTypes.AgreementOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.createAgreement(payload.agreement).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (!res.id) {
            return new ThrowAgreementError({ error: res });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementCreated }),
            );
            return new AgreementCreated({ agreement: res });
          }
        }),
      );
    }),
  );

  //

  @Effect()
  remindAgreement = this.actions$.pipe(
    ofType<AgreementReminderRequested>(AgreementActionTypes.AgreementReminderRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.remindAgreement(payload.agreement.id).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowAgreementError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementReminded }),
            );
            return new AgreementReminded(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  revokeAgreement = this.actions$.pipe(
    ofType<RevokeAgreementRequested>(AgreementActionTypes.RevokeAgreementRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.agreementService.revokeAgreement(payload.agreement.id, payload.reason).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowAgreementError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(
              new FireAgreementSuccess({ action_type: AgreementActionTypes.AgreementRevoked }),
            );
            return new AgreementRevoked(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  init$: Observable<Action> = defer(() => {
    const allAgreement = localStorage.getItem('allAgreement');
    if (allAgreement) {
      return of(
        new AllAgreementsLoaded({
          agreements: JSON.parse(allAgreement),
        }),
      );
    }
    return of(new AllAgreementsRequested());
  });

  constructor(
    private actions$: Actions,
    private agreementService: AgreementService,
    private store: Store<AppState>,
  ) {}
}
